import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Plus, X, Plane } from "lucide-react";

// Lazy-load CBP overlays — they pull in pdf.js (~hundreds of KB) for the
// PDF viewer but only render when the user actually clicks the CBP info
// card. Keeping them out of the modal's eager bundle cuts open-time.
const CBPDetailsModal = lazy(() =>
  import("@/components/cbp/CBPShared").then((m) => ({ default: m.CBPDetailsModal }))
);
const CBPPdfModal = lazy(() =>
  import("@/components/cbp/CBPShared").then((m) => ({ default: m.CBPPdfModal }))
);
import { useAuth } from "@/contexts/AuthContext";
import { useDraftAutosave } from "@/hooks/useDraftAutosave";
import { useAltivModal } from "@/hooks/useAltivModal";
import { DEFAULT_LEG, clonePeopleFromLeg } from "@/lib/newTripConstants";
import useCbpEnrichment from "@/hooks/new-trip/useCbpEnrichment";
import useMxCustomsEnrichment from "@/hooks/new-trip/useMxCustomsEnrichment";
import useBaseAirport from "@/hooks/new-trip/useBaseAirport";
import useAogDates from "@/hooks/new-trip/useAogDates";
import useLegHandlers from "@/hooks/new-trip/useLegHandlers";
import TripHeaderSection from "./TripHeaderSection";
import LegSection from "./LegSection";
import ConflictBanners from "./ConflictBanners";
import useAircraftChange from "./hooks/useAircraftChange";
import useTripSubmit from "./hooks/useTripSubmit";
import { derivePassportConflicts } from "./utils";
import { paxFullName } from "@/lib/newTripConstants";
import FocusTrap from "@/components/common/FocusTrap";

// Module-level frozen empty array so useMemo callers can return a stable
// reference when there are no conflicts — otherwise every keystroke produces
// a fresh `[]` and breaks downstream memo barriers (ConflictBanners etc.).
const EMPTY_ARR = Object.freeze([]);

export default function NewTripModal({
  aircraft,
  userRole,
  onClose,
  onSuccess,
  appendToTrip = null,
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAppendMode = !!appendToTrip;
  const [cbpDetailsLegIdx, setCbpDetailsLegIdx] = useState(null);
  const [cbpPdfLegIdx, setCbpPdfLegIdx] = useState(null);
  const [legs, setLegs] = useState(() => [
    {
      ...DEFAULT_LEG,
      _client_id: `leg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      origin: appendToTrip?.last_destination || "",
    },
  ]);
  // Default 'pending' para TODOS los roles. Owner es quien decide si el viaje va
  // o no; ops_manager solo coordina, así que no debería poder "auto-confirmar"
  // al crear sin que owner se entere. Append heredaba el status del trip padre.
  const [tripStatus, setTripStatus] = useState(appendToTrip?.status || "pending");
  const [expandedLeg, setExpandedLeg] = useState(0);
  const [userFuelUnit, setUserFuelUnit] = useState("lbs");
  const [extCrewPickerLeg, setExtCrewPickerLeg] = useState(null);

  // calculateETA lives on useLegHandlers; useAircraftChange's recompute path
  // needs a stable reference to it. We forward via a ref so the cascade hook
  // doesn't have to re-build itself every render.
  const calcEtaRef = React.useRef(null);
  const calculateETAStable = useCallback((...args) => calcEtaRef.current?.(...args), []);

  // resetDocsConfirmed lives on useTripSubmit (declared below). Stash in a
  // ref so useAircraftChange can re-arm the docs warning without a circular
  // hook dependency.
  const resetDocsRef = React.useRef(null);
  // Stable ref so useAircraftChange's useCallbacks don't churn every render.
  const onPassengerSetChanged = useCallback(() => resetDocsRef.current?.(), []);

  const {
    aircraftId,
    setAircraftId,
    aircraftOwners,
    selectedOwner,
    setSelectedOwner,
    assignedCrew,
    crewLoadError,
    temporaryCrew,
    ownerPassengers,
    crewPassportMap,
    onAircraftChange,
    loadOwnerPassengers,
  } = useAircraftChange({
    setLegs,
    calculateETA: calculateETAStable,
    onPassengerSetChanged,
  });

  const { baseAirport, baseForTz } = useBaseAirport();
  const aogDateSet = useAogDates(aircraftId);
  useCbpEnrichment(legs, setLegs);
  useMxCustomsEnrichment(legs, setLegs);

  const draftSnapshot = useMemo(
    () => ({ aircraftId, legs, tripStatus, selectedOwner }),
    [aircraftId, legs, tripStatus, selectedOwner]
  );
  // The `isEmpty: () => true` / `enabled: !isAppendMode` combo is a
  // deliberate "modal is open" sentinel for the deferred-reload system, NOT
  // real autosave logic. Refactoring useDraftAutosave to expose a proper
  // markBusy() API is out of scope (same hook is shared by FlightDetailModal).
  const isEmptyDraft = (s) =>
    !s.aircraftId &&
    !s.selectedOwner &&
    Array.isArray(s.legs) &&
    s.legs.length === 1 &&
    !s.legs[0].origin &&
    !s.legs[0].destination &&
    !s.legs[0].departure_date;
  const { savedDraft, clear: clearDraft } = useDraftAutosave("newTrip", draftSnapshot, {
    isEmpty: isEmptyDraft,
    enabled: !isAppendMode,
  });
  const handleManualClose = useCallback(() => {
    clearDraft();
    onClose?.();
  }, [clearDraft, onClose]);
  useAltivModal({ open: true, onClose: handleManualClose });

  useEffect(() => {
    if (isAppendMode) return;
    if (!savedDraft?.data) return;
    const d = savedDraft.data;
    if (d.aircraftId) setAircraftId(d.aircraftId);
    if (Array.isArray(d.legs) && d.legs.length > 0) setLegs(d.legs);
    if (d.tripStatus) setTripStatus(d.tripStatus);
    if (d.selectedOwner) setSelectedOwner(d.selectedOwner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owner self-service: a trip an owner creates is always theirs, so the owner
  // picker is hidden (TripHeaderSection) and we auto-assign the logged-in owner
  // here — both to pull their saved-passenger suggestions and so the request
  // carries the right owner_id. The backend independently forces owner_id to
  // the session owner for the "owner" role (authoritative; can't be spoofed).
  useEffect(() => {
    if (isAppendMode) return;
    if (userRole !== "owner" || !user?.owner_id) return;
    setSelectedOwner(user.owner_id);
    loadOwnerPassengers(user.owner_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, user?.owner_id]);

  useEffect(() => {
    if (!isAppendMode || !appendToTrip?.aircraft_id) return;
    onAircraftChange(appendToTrip.aircraft_id);
    if (appendToTrip.owner_id) setSelectedOwner(appendToTrip.owner_id);
    // El primer tramo del append nace heredando pax+tripulación del último
    // tramo del viaje padre (ambos toggles en ON). El usuario los apaga desde
    // el banner DENTRO del tramo.
    if (appendToTrip.prev_leg) {
      const cloned = clonePeopleFromLeg(appendToTrip.prev_leg, { pax: true, crew: true }) || {};
      setLegs((prev) =>
        prev.map((leg, i) =>
          i === 0 ? { ...leg, inherit_pax: true, inherit_crew: true, ...cloned } : leg
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Body scroll lock + html data-attr lo cubre useAltivModal arriba.)

  const availableAircraft = useMemo(
    () => aircraft.filter((a) => a.status !== "maintenance"),
    [aircraft]
  );

  const legHandlers = useLegHandlers({
    legs,
    setLegs,
    expandedLeg,
    setExpandedLeg,
    aircraftId,
    temporaryCrew,
    setExtCrewPickerLeg,
  });
  calcEtaRef.current = legHandlers.calculateETA;
  const {
    updateLeg,
    addLeg,
    removeLeg,
    setPassengerCount,
    updatePassenger,
    removePassenger,
    selectSuggestedPassenger,
    toggleCrewMember,
    updateCrewFlightRole,
    addExternalCrew,
    addExternalCrewFromTemp,
    openExtCrewPicker,
    updateExternalCrew,
    setExternalCrewFields,
    removeExternalCrew,
    isCrewSelected,
  } = legHandlers;

  // Scroll container (the modal body). Al "Agregar tramo" llevamos el scroll
  // EXACTO al inicio del tramo recién creado (no al tope del modal), para que
  // se pueda empezar a llenarlo de inmediato sin tener que bajar de nuevo.
  // El tramo nuevo aún no está en el DOM cuando se hace clic, así que marcamos
  // un pendiente y el efecto de abajo hace el scroll una vez renderizado.
  const modalBodyRef = useRef(null);
  const pendingScrollToNewLeg = useRef(false);
  const handleAddLeg = useCallback(() => {
    addLeg();
    pendingScrollToNewLeg.current = true;
  }, [addLeg]);

  useEffect(() => {
    if (!pendingScrollToNewLeg.current) return;
    pendingScrollToNewLeg.current = false;
    const body = modalBodyRef.current;
    if (!body) return;
    const newLegEl = body.querySelector(`[data-testid="new-trip-leg-${legs.length - 1}"]`);
    if (!newLegEl) return;
    // Alinear el borde superior del tramo nuevo con el tope del área visible
    // (con un pelín de aire arriba) en vez de saltar a top:0.
    const delta = newLegEl.getBoundingClientRect().top - body.getBoundingClientRect().top;
    body.scrollTo({ top: body.scrollTop + delta - 8, behavior: "smooth" });
  }, [legs.length]);

  const selectedAircraft = useMemo(
    () => aircraft.find((a) => a.id === aircraftId),
    [aircraft, aircraftId]
  );
  const currentOwnerPassengers = useMemo(
    () => (selectedOwner ? ownerPassengers[selectedOwner] || [] : []),
    [selectedOwner, ownerPassengers]
  );

  const internationalPassportConflicts = useMemo(() => {
    const result = derivePassportConflicts(legs, crewPassportMap);
    return result.length === 0 ? EMPTY_ARR : result;
  }, [legs, crewPassportMap]);

  const hasIntlLeg = useMemo(() => legs.some((l) => l.is_international), [legs]);
  const ownerIntlMissing = useMemo(() => {
    if (userRole !== "owner" || !hasIntlLeg) return EMPTY_ARR;
    const result = legs.flatMap((leg, li) => {
      if (!leg.is_international || leg.is_ferry_flight) return [];
      return (leg.passengers || []).flatMap((pax) => {
        const fullName = paxFullName(pax);
        if (!fullName) return [];
        if (!pax.passport_number?.trim() || !pax.passport_expiration) {
          return [`Tramo ${li + 1} – Pasajero "${fullName}": pasaporte incompleto`];
        }
        return [];
      });
    });
    return result.length === 0 ? EMPTY_ARR : result;
  }, [legs, userRole, hasIntlLeg]);

  const {
    submitTrip,
    error,
    aogConflict,
    scheduleConflict,
    setScheduleConflict,
    forceSchedule,
    setForceSchedule,
    isSubmitting,
    resetDocsConfirmed,
  } = useTripSubmit({
    legs,
    aircraftId,
    selectedOwner,
    tripStatus,
    userRole,
    temporaryCrew,
    internationalPassportConflicts,
    appendToTrip,
    t,
    clearDraft,
    onSuccess,
    onValidationError: setExpandedLeg,
    // Append that failed midway: drop the legs that already committed so a
    // retry only sends the ones still pending (no duplicate re-post).
    onLegsCommitted: (count) => setLegs((prev) => prev.slice(count)),
  });
  resetDocsRef.current = resetDocsConfirmed;

  // Re-arm the docs precheck warning whenever a passenger field changes:
  // editing/adding/removing a passenger after the manager already accepted
  // the warning must surface it again if the new state still triggers it.
  const setPassengerCountW = useCallback(
    (...args) => {
      resetDocsConfirmed();
      setPassengerCount(...args);
    },
    [resetDocsConfirmed, setPassengerCount]
  );
  const updatePassengerW = useCallback(
    (...args) => {
      resetDocsConfirmed();
      updatePassenger(...args);
    },
    [resetDocsConfirmed, updatePassenger]
  );
  const removePassengerW = useCallback(
    (...args) => {
      resetDocsConfirmed();
      removePassenger(...args);
    },
    [resetDocsConfirmed, removePassenger]
  );
  const selectSuggestedPassengerW = useCallback(
    (...args) => {
      resetDocsConfirmed();
      selectSuggestedPassenger(...args);
    },
    [resetDocsConfirmed, selectSuggestedPassenger]
  );

  // submitTrip's identity changes whenever `legs` changes (per-keystroke).
  // Stash in a ref so onForceSubmit can stay stable across renders — without
  // this, ConflictBanners' memo barrier breaks on every keystroke.
  const submitTripRef = React.useRef(submitTrip);
  submitTripRef.current = submitTrip;
  const onForceSubmit = useCallback(
    () => submitTripRef.current(null, { forceScheduleOverride: true }),
    []
  );

  // Banner "Copiar del tramo anterior" — ahora vive DENTRO de cada tramo y es
  // una decisión per-tramo. Al alternar un toggle, re-derivamos pax/tripulación
  // SOLO de ese tramo desde su fuente (el tramo previo; o el último tramo del
  // viaje padre cuando es el tramo 0 en modo append). Apagar limpia esa dimensión.
  const blankPax = useCallback(
    () => ({
      first_name: "",
      middle_name: "",
      last_name: "",
      passport_number: "",
      passport_expiration: "",
      passport_country: "",
      dob: "",
      address: "",
    }),
    []
  );
  const setLegInherit = useCallback(
    (legIndex, key, value) => {
      // Cambiar pasajeros re-arma el aviso de documentos (igual que editar pax).
      if (key === "pax") resetDocsRef.current?.();
      setLegs((prev) =>
        prev.map((leg, i) => {
          if (i !== legIndex) return leg;
          const source = legIndex === 0 ? appendToTrip?.prev_leg : prev[legIndex - 1];
          if (key === "pax") {
            const updated = { ...leg, inherit_pax: value };
            if (value) {
              const cloned = clonePeopleFromLeg(source, { pax: true, crew: false }) || {};
              const passengers = cloned.passengers?.length ? cloned.passengers : [blankPax()];
              updated.passengers = passengers;
              updated.passenger_count = passengers.length;
              updated.is_ferry_flight = !!cloned.is_ferry_flight;
            } else {
              updated.passengers = [blankPax()];
              updated.passenger_count = 1;
            }
            return updated;
          }
          const updated = { ...leg, inherit_crew: value };
          if (value) {
            const cloned = clonePeopleFromLeg(source, { pax: false, crew: true }) || {};
            updated.crew = cloned.crew || [];
            updated.external_crew = cloned.external_crew || [];
          } else {
            updated.crew = [];
            updated.external_crew = [];
          }
          return updated;
        })
      );
    },
    [appendToTrip, blankPax]
  );
  const closeCbpDetails = useCallback(() => setCbpDetailsLegIdx(null), []);
  const closeCbpPdf = useCallback(() => setCbpPdfLegIdx(null), []);
  const cbpDetailInfo = cbpDetailsLegIdx != null ? legs[cbpDetailsLegIdx]?.cbp_info || null : null;
  const cbpPdfInfo = cbpPdfLegIdx != null ? legs[cbpPdfLegIdx]?.cbp_info || null : null;

  return createPortal(
    <FocusTrap
      className="altiv-modal-shell"
      role="dialog"
      aria-modal="true"
      data-testid="new-trip-modal"
    >
      <div className="altiv-modal-card sm:max-w-3xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="altiv-modal-header px-6 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Plane className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isAppendMode
                  ? t("newTrip.appendTitle")
                  : userRole === "owner"
                    ? t("newTrip.requestTrip")
                    : t("newTrip.scheduleNewTrip")}
              </h2>
              <p className="text-xs text-slate-400">
                {isAppendMode
                  ? t("newTrip.appendSubtitle")
                  : userRole === "owner"
                    ? t("newTrip.requestSubtitle")
                    : t("newTrip.scheduleSubtitle")}
              </p>
            </div>
          </div>
          <button
            onClick={handleManualClose}
            className="flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 rounded-lg bg-slate-700/60 text-slate-300 hover:bg-red-500/20 hover:text-red-400 active:bg-red-500/20 active:text-red-400 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submitTrip} className="altiv-modal-body" ref={modalBodyRef}>
          <div className="p-6 space-y-6">
            <TripHeaderSection
              isAppendMode={isAppendMode}
              availableAircraft={availableAircraft}
              selectedAircraft={selectedAircraft}
              aircraftId={aircraftId}
              onAircraftChange={onAircraftChange}
              userRole={userRole}
              tripStatus={tripStatus}
              setTripStatus={setTripStatus}
              aircraftOwners={aircraftOwners}
              selectedOwner={selectedOwner}
              setSelectedOwner={setSelectedOwner}
              loadOwnerPassengers={loadOwnerPassengers}
              t={t}
            />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs text-slate-400 uppercase tracking-wide">
                  {t("newTrip.tripLegsCount", { count: legs.length })}
                </label>
              </div>

              {legs.map((leg, legIndex) => (
                <LegSection
                  key={leg._client_id || legIndex}
                  leg={leg}
                  legIndex={legIndex}
                  legsCount={legs.length}
                  prevLegDepartureDate={
                    legIndex > 0 ? legs[legIndex - 1]?.departure_date : undefined
                  }
                  expandedLeg={expandedLeg}
                  setExpandedLeg={setExpandedLeg}
                  removeLeg={removeLeg}
                  updateLeg={updateLeg}
                  setLegs={setLegs}
                  setLegInherit={setLegInherit}
                  userRole={userRole}
                  isAppendMode={isAppendMode}
                  aircraftId={aircraftId}
                  aogDateSet={aogDateSet}
                  baseAirport={baseAirport}
                  baseForTz={baseForTz}
                  userFuelUnit={userFuelUnit}
                  setUserFuelUnit={setUserFuelUnit}
                  currentOwnerPassengers={currentOwnerPassengers}
                  setPassengerCount={setPassengerCountW}
                  updatePassenger={updatePassengerW}
                  removePassenger={removePassengerW}
                  selectSuggestedPassenger={selectSuggestedPassengerW}
                  assignedCrew={assignedCrew}
                  crewLoadError={crewLoadError}
                  temporaryCrew={temporaryCrew}
                  extCrewPickerLeg={extCrewPickerLeg}
                  setExtCrewPickerLeg={setExtCrewPickerLeg}
                  openExtCrewPicker={openExtCrewPicker}
                  addExternalCrew={addExternalCrew}
                  addExternalCrewFromTemp={addExternalCrewFromTemp}
                  updateExternalCrew={updateExternalCrew}
                  setExternalCrewFields={setExternalCrewFields}
                  removeExternalCrew={removeExternalCrew}
                  toggleCrewMember={toggleCrewMember}
                  updateCrewFlightRole={updateCrewFlightRole}
                  isCrewSelected={isCrewSelected}
                  onOpenCbpDetails={setCbpDetailsLegIdx}
                  onOpenCbpPdf={setCbpPdfLegIdx}
                  t={t}
                  i18n={i18n}
                />
              ))}

              <button
                type="button"
                onClick={handleAddLeg}
                disabled={!aircraftId}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-bold rounded-lg bg-[#0052FF]/15 text-[#0052FF] border border-dashed border-[#0052FF]/30 hover:bg-[#0052FF]/25 hover:border-[#0052FF]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3.5 h-3.5" /> {t("newTrip.addLegBtn")}
              </button>
            </div>
          </div>

          <ConflictBanners
            hasIntlLeg={hasIntlLeg}
            ownerIntlMissing={ownerIntlMissing}
            userRole={userRole}
            internationalPassportConflicts={internationalPassportConflicts}
            error={error}
            aogConflict={aogConflict}
            scheduleConflict={scheduleConflict}
            forceSchedule={forceSchedule}
            setForceSchedule={setForceSchedule}
            setScheduleConflict={setScheduleConflict}
            submitting={isSubmitting}
            onForceSubmit={onForceSubmit}
            t={t}
          />
          {!(scheduleConflict && scheduleConflict.length > 0 && !forceSchedule) && (
            <div className="px-6 py-4 border-t border-slate-800 flex gap-3 flex-shrink-0 bg-[#1e293b]">
              <button
                type="button"
                onClick={handleManualClose}
                className="flex-1 px-4 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !aircraftId ||
                  (internationalPassportConflicts.length > 0 && userRole !== "owner")
                }
                className="flex-1 px-4 py-2.5 bg-[#0052FF] text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Plane className="w-4 h-4" />
                {isSubmitting
                  ? isAppendMode
                    ? t("newTrip.appendingLeg")
                    : t("newTrip.creating")
                  : isAppendMode
                    ? t("newTrip.appendCta")
                    : t("newTrip.createTrip")}
              </button>
            </div>
          )}
        </form>
      </div>
      {cbpDetailInfo && (
        <Suspense fallback={null}>
          <CBPDetailsModal
            detail={cbpDetailInfo}
            base={baseForTz}
            t={t}
            onClose={closeCbpDetails}
          />
        </Suspense>
      )}
      {cbpPdfInfo && (
        <Suspense fallback={null}>
          <CBPPdfModal
            icao={cbpPdfInfo.icao}
            iata={cbpPdfInfo.iata}
            fetchedAt={cbpPdfInfo.fetched_at}
            t={t}
            onClose={closeCbpPdf}
          />
        </Suspense>
      )}
    </FocusTrap>,
    document.body
  );
}
